// ============================================
// Detail Module - Modal (ES6)
// 콘텐츠 상세 모달 UI
// ============================================

import {
  setSelectedContent, getSelectedContent, getDb,
  getAllPublishedContent, getMyPageWorks
} from './state.js';
import { updateLikeButtonState } from './like.js';
import { updateDetailActionButtons } from './actions.js';
import { getCurrentUser } from '../../app/state.js';
import { showLoginModal } from '../../auth/index.js';
import {
  showToast, escapeHtml, showGlobalLoading, hideGlobalLoading, formatPlayCount, sanitizeHtml
} from '../../utils.js';
import { recordClick } from '../../analytics/stats-buffer.js';
import { loadSimilarContent, renderHomeCard } from '../content.js';

// ============================================
// 콘텐츠 상세 모달
// ============================================

export async function showContentDetail(contentIdOrData) {
  // 비로그인 시 로그인 모달 띄우기
  if (!getCurrentUser()) {
    showLoginModal();
    return;
  }

  let content;

  const db = getDb();

  if (typeof contentIdOrData === 'string') {
    // ID로 전달된 경우 - 기존 방식
    content = getAllPublishedContent().find(c => c.id === contentIdOrData);
    // 캐시에 없으면 myPageWorks에서 찾기 (내 작품)
    if (!content) {
      const myWorks = getMyPageWorks();
      if (myWorks && myWorks.length > 0) {
        content = myWorks.find(c => c.id === contentIdOrData);
      }
    }
    // 캐시에도 없으면 Firestore에서 직접 로드 시도
    if (!content) {
      try {
        const docSnap = await db.collection('published').doc(contentIdOrData).get();
        if (!docSnap.exists) {
          showToast('콘텐츠를 찾을 수 없습니다', 'error');
          return;
        }
        content = { id: contentIdOrData, ...docSnap.data() };
      } catch (err) {
        if (err.code === 'permission-denied') {
          showToast('접근이 불가능한 점술가입니다', 'error');
        } else {
          showToast('콘텐츠를 찾을 수 없습니다', 'error');
        }
        return;
      }
    }
  } else if (typeof contentIdOrData === 'object' && contentIdOrData !== null) {
    // 데이터 객체로 전달된 경우 (내 작품 등)
    content = contentIdOrData;
    // 필수 필드 정규화
    content.title = content.characterName || content.title || '이름 없음';
    content.type = content.type || 'character';
    content.tags = content.tags || content.hashtags || [];
    content.shortDesc = content.shortIntro || content.shortDesc || '';
    content.creatorNickname = content.authorNickname || content.authorName || '크리에이터';
  } else {
    showToast('콘텐츠를 찾을 수 없습니다', 'error');
    return;
  }

  setSelectedContent(content);

  // 클릭 통계 기록
  if (content.id) {
    recordClick(content.id);
  }

  // Show modal with slide animation
  const modalOverlay = document.getElementById('contentDetailModal');
  const detailModal = document.querySelector('.content-detail-modal');
  modalOverlay.style.display = 'flex';

  // z-index 동적 관리
  window.ModalManager?.push('contentDetailModal');
  // 스크롤 최상단으로 이동 및 상단바 초기화
  detailModal.scrollTop = 0;
  document.querySelector('.detail-header').classList.remove('scrolled');
  // 슬라이드 애니메이션 트리거 (.content-detail-modal에 active 추가)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      detailModal.classList.add('active');
    });
  });

  // 이전 데이터 초기화 (로딩 중 이전 내용이 보이지 않도록)
  document.getElementById('detailImage').innerHTML = '';
  document.getElementById('detailImageThumbnails').innerHTML = '';
  document.getElementById('detailImageThumbnails').classList.remove('has-multiple');
  document.getElementById('detailTitle').textContent = '';
  document.getElementById('detailHeaderTitle').textContent = '';
  document.getElementById('detailDescription').textContent = '';
  document.getElementById('detailTags').innerHTML = '';
  document.getElementById('detailPlayCount').textContent = '0';
  document.getElementById('detailCharPrompt').innerHTML = '';
  document.getElementById('detailWorldPrompt').innerHTML = '';
  document.getElementById('detailMainActions').innerHTML = '';

  showGlobalLoading();

  // 상세 데이터 로드 (서브컬렉션에서 - 객체로 전달되어도 상세 데이터 없으면 로드)
  if (!content.character && !content.firstMessage && content.id) {
    try {
      const detailDoc = await db.collection('published').doc(content.id)
        .collection('content').doc('detailData').get();
      if (detailDoc.exists) {
        const detailData = detailDoc.data();
        content = { ...content, ...detailData };
        setSelectedContent(content);
      }
    } catch (err) {
      // 권한 에러 (비공개 콘텐츠) 처리
      if (err.code === 'permission-denied') {
        hideGlobalLoading();
        closeContentDetailModal();
        showToast('접근이 불가능한 점술가입니다', 'error');
        return;
      }
      console.warn('상세 데이터 로드 실패:', err);
    }
  }

  const isCharacter = content.type === 'character';
  const typeLabel = isCharacter ? '점술가' : '스토리';

  // Image - 복수 이미지 지원
  const imageEl = document.getElementById('detailImage');
  const thumbsEl = document.getElementById('detailImageThumbnails');

  // 이미지 배열 수집 (images 배열 또는 imageUrl)
  let allImages = [];
  if (content.images && Array.isArray(content.images) && content.images.length > 0) {
    allImages = content.images;
  } else if (content.imageUrl) {
    allImages = [content.imageUrl];
  }

  // 메인 이미지 렌더링 함수
  const renderMainImage = (url) => {
    imageEl.innerHTML = `<div class="img-loading-wrapper" style="width:100%;height:100%"><div class="spinner"></div><img src="${url}" alt="${escapeHtml(content.title)}" style="width:100%;height:100%;object-fit:cover;object-position:center" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')"></div>`;
  };

  if (allImages.length > 0) {
    renderMainImage(allImages[0]);

    // 복수 이미지일 때 썸네일 표시
    if (allImages.length > 1) {
      thumbsEl.classList.add('has-multiple');
      thumbsEl.innerHTML = allImages.map((url, idx) => `
        <div class="detail-thumb-item${idx === 0 ? ' active' : ''}" data-index="${idx}" data-url="${url}">
          <img src="${url}" alt="이미지 ${idx + 1}">
        </div>
      `).join('');

      // 썸네일 클릭 이벤트
      thumbsEl.addEventListener('click', (e) => {
        const thumb = e.target.closest('.detail-thumb-item');
        if (!thumb) return;

        const url = thumb.dataset.url;
        renderMainImage(url);

        // 활성 상태 업데이트
        thumbsEl.querySelectorAll('.detail-thumb-item').forEach(el => el.classList.remove('active'));
        thumb.classList.add('active');
      });
    }
  } else {
    imageEl.innerHTML = isCharacter ? '👤' : '📖';
  }

  // Badges (언필터는 safetyFilter가 false이거나 unfiltered가 true일 때 + 타입)
  const showUnfilter = content.safetyFilter === false || content.unfiltered === true;
  document.getElementById('detailUnfilterBadge').style.display = showUnfilter ? 'inline-flex' : 'none';
  document.getElementById('detailTypeBadge').textContent = typeLabel;

  // Title (본문 + 상단바)
  const titleText = content.title || '제목 없음';
  document.getElementById('detailTitle').textContent = titleText;
  document.getElementById('detailHeaderTitle').textContent = titleText;

  // Description (이름 바로 아래)
  document.getElementById('detailDescription').textContent = content.shortDesc || content.description || '';

  // Tags (회색 텍스트)
  const tagsEl = document.getElementById('detailTags');
  const tags = content.tags || [];
  if (tags.length > 0) {
    tagsEl.innerHTML = tags.map(tag => `#${escapeHtml(tag.replace(/^#/, ''))}`).join(' ');
    tagsEl.style.display = 'block';
  } else {
    tagsEl.style.display = 'none';
  }

  // Chat count badge
  document.getElementById('detailPlayCount').textContent = formatPlayCount(content.chatCount) || 0;

  // Like count
  document.getElementById('detailLikeCount').textContent = content.likeCount || 0;

  // 대표 프로필 가져오기 ({{user}} 파싱용)
  const primaryProfile = (typeof Auth !== 'undefined' && Auth.getPrimaryProfile) ? Auth.getPrimaryProfile() : null;
  const primaryUserName = primaryProfile?.name || null;

  // {{user}} 파싱 헬퍼 함수
  const parseUserMarker = (text) => {
    if (!text) return text;
    if (primaryUserName) {
      return text.replace(/\{\{user\}\}/gi, primaryUserName);
    }
    return text;
  };

  // 캐릭터 데이터 먼저 추출
  const char = content.character || {};
  const charPrompt = char.prompt || '';
  const worldPrompt = content.worldPrompt || '';
  const charDescription = content.characterDescription || '';

  // 캐릭터 프롬프트 섹션 (커스텀 설명이 있으면 커스텀 설명, 없으면 프롬프트)
  const charPromptEl = document.getElementById('detailCharPrompt');
  const charPromptDivider = charPromptEl.previousElementSibling;

  if (isCharacter && charDescription) {
    // 커스텀 설명 표시
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(charDescription);
    if (hasHtmlTags) {
      // HTML: sanitizeHtml로 XSS 방지 후 렌더링
      charPromptEl.innerHTML = sanitizeHtml(parseUserMarker(charDescription));
    } else {
      // 텍스트: escapeHtml로 이스케이프 후 프롬프트 디자인으로 표시
      const parsed = escapeHtml(parseUserMarker(charDescription)).replace(/\n/g, '<br>');
      charPromptEl.innerHTML = `
        <div class="detail-char-section">
          <div class="detail-char-section-title">점술가 소개</div>
          <div class="detail-char-section-content detail-prompt">${parsed}</div>
        </div>
      `;
    }
    charPromptEl.style.display = 'block';
    charPromptDivider.style.display = 'block';
  } else if (isCharacter && charPrompt) {
    // 프롬프트 표시
    let parsedPrompt = parseUserMarker(charPrompt);
    parsedPrompt = escapeHtml(parsedPrompt).replace(/\n/g, '<br>');
    charPromptEl.innerHTML = `
      <div class="detail-char-section">
        <div class="detail-char-section-title">점술가 프롬프트</div>
        <div class="detail-char-section-content detail-prompt">${parsedPrompt}</div>
      </div>
    `;
    charPromptEl.style.display = 'block';
    charPromptDivider.style.display = 'block';
  } else {
    charPromptEl.style.display = 'none';
    charPromptDivider.style.display = 'none';
  }

  // 세계관 프롬프트 섹션 (커스텀 설명이 없을 때만 표시)
  const worldPromptEl = document.getElementById('detailWorldPrompt');
  const worldPromptDivider = worldPromptEl.previousElementSibling;

  if (isCharacter && worldPrompt && !charDescription) {
    let parsedWorld = parseUserMarker(worldPrompt);
    parsedWorld = escapeHtml(parsedWorld).replace(/\n/g, '<br>');
    worldPromptEl.innerHTML = `
      <div class="detail-char-section">
        <div class="detail-char-section-title">세계관 프롬프트</div>
        <div class="detail-char-section-content detail-prompt">${parsedWorld}</div>
      </div>
    `;
    worldPromptEl.style.display = 'block';
    worldPromptDivider.style.display = 'block';
  } else {
    worldPromptEl.style.display = 'none';
    worldPromptDivider.style.display = 'none';
  }

  // 제작자 코멘트 섹션
  const creatorCommentEl = document.getElementById('detailCreatorComment');
  const creatorCommentDivider = document.getElementById('detailCreatorCommentDivider');
  const creatorComment = content.creatorComment || '';

  if (creatorComment && creatorCommentEl) {
    // HTML 태그 포함 여부 확인
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(creatorComment);
    let rendered;
    if (hasHtmlTags) {
      // HTML 있음: sanitize
      rendered = sanitizeHtml(creatorComment);
    } else {
      // 일반 텍스트: escape 후 줄바꿈 변환
      rendered = escapeHtml(creatorComment).replace(/\n/g, '<br>');
    }

    creatorCommentEl.innerHTML = `
      <div class="detail-section-title">제작자 코멘트</div>
      <div class="detail-creator-comment-content detail-custom-desc">${rendered}</div>
    `;
    creatorCommentEl.style.display = 'block';
    if (creatorCommentDivider) creatorCommentDivider.style.display = 'block';
  } else if (creatorCommentEl) {
    creatorCommentEl.style.display = 'none';
    if (creatorCommentDivider) creatorCommentDivider.style.display = 'none';
  }

  // 비슷한 캐릭터 섹션 (비동기 로드)
  renderSimilarSection(content.id);

  // Like button state & Action buttons (with error handling)
  try {
    await updateLikeButtonState(content.id);
    await updateDetailActionButtons(content.id);
  } catch (e) {
    console.error('Error updating detail buttons:', e);
    // 비로그인 시 기본 버튼 표시
    if (!getCurrentUser()) {
      document.getElementById('detailMainActions').innerHTML = `
        <button class="detail-action-btn primary" data-action="login">채팅하기</button>
      `;
    }
  } finally {
    // 항상 로딩 숨기기
    hideGlobalLoading();
  }
}

// ============================================
// 모달 닫기
// ============================================

export function closeContentDetailModal() {
  const modalOverlay = document.getElementById('contentDetailModal');
  const detailModal = document.querySelector('.content-detail-modal');
  detailModal.classList.remove('active');
  document.querySelector('.detail-header').classList.remove('scrolled');
  // 트랜지션 완료 후 display: none
  setTimeout(() => {
    if (!detailModal.classList.contains('active')) {
      modalOverlay.style.display = 'none';
    }
  }, 300);
  setSelectedContent(null);

  // z-index 스택에서 제거
  window.ModalManager?.remove('contentDetailModal');
}

// ============================================
// 비슷한 캐릭터 섹션 렌더링
// ============================================

async function renderSimilarSection(contentId) {
  const section = document.getElementById('detailSimilarSection');
  const divider = document.getElementById('detailSimilarDivider');

  if (!section || !divider) return;

  // 초기화 (로딩 중 이전 데이터 숨김)
  section.style.display = 'none';
  divider.style.display = 'none';

  try {
    const similar = await loadSimilarContent(contentId, 6);

    if (!similar.length) return;

    section.innerHTML = `
      <div class="detail-similar-title">이 점술가와 비슷한 점술가들이에요</div>
      <div class="detail-similar-scroll">
        ${similar.map(item => renderHomeCard(item)).join('')}
      </div>
    `;

    // 카드 클릭 이벤트 위임
    section.onclick = (e) => {
      const card = e.target.closest('.home-card-wrapper[data-content-id]');
      if (!card) return;

      const clickedId = card.dataset.contentId;
      // 현재 보고 있는 캐릭터와 같으면 무시
      if (clickedId === contentId) return;

      // 다른 캐릭터 상세 모달 열기
      showContentDetail(clickedId);
    };

    section.style.display = 'block';
    divider.style.display = 'block';
  } catch (e) {
    console.error('[Detail] 비슷한 캐릭터 로드 오류:', e);
  }
}
